import { useMemo } from 'react';
import { getText } from 'i18n/intl';
import { Form, Select } from '@timeedit/ui-components/antd';
import { LabelWrapper } from '@timeedit/ui-components/components/InputComponent/Input';
import {
  type BulkEditAction,
  type BulkEditFieldType,
  BulkEditMatchRule,
  type CommonProps,
  getBulkEditFieldOptions,
  getBulkEditSelectProps,
} from './common';

type Props = CommonProps & {
  actionList?: BulkEditAction[];
};

const BulkEditCategory = ({ field, form, actionList }: Props) => {
  const value = Form.useWatch(field.id, form);

  const showAdd = value === 'add' && field.multiple;
  const showFindAndReplace = value === 'findReplace';
  const showReplaceAll = value === 'replaceAll';
  const showRemoveSpecific = value === 'removeSpecific';

  const findReplacePointless =
    (field.categories ?? []).length === 0 && field.editable === true;

  const options = useMemo(
    () =>
      getBulkEditFieldOptions(
        actionList ?? [
          'add',
          'keep',
          'findReplace',
          'replaceAll',
          'removeSpecific',
          'removeAll',
        ],
        field,
        findReplacePointless ? ['findReplace', 'removeSpecific'] : []
      ),
    [field.multiple, field.unique, field.mandatory, findReplacePointless]
  );

  const noValidOptions =
    (field.categories?.length ?? 0) === 0 && field.editable !== true;

  return (
    <LabelWrapper
      label={field.name}
      tooltip={
        noValidOptions
          ? getText('object_manager.bulk-edit-modal.tooltip.no-categories')
          : field.description
      }
    >
      <div className="bulk-edit-input-wrapper">
        <Form.Item required name={field.id}>
          <Select
            defaultValue="keep"
            options={options}
            disabled={noValidOptions}
          />
        </Form.Item>
        {showAdd && <Add field={field} />}
        {showFindAndReplace && <FindAndReplace field={field} />}
        {showReplaceAll && <ReplaceAll field={field} />}
        {showRemoveSpecific && <RemoveSpecific field={field} />}
      </div>
    </LabelWrapper>
  );
};

const mapCategoriesToOptions = (
  categories?: string[],
  disabledValues?: string[]
) => {
  return categories?.map((cat) => ({
    label: cat,
    value: cat,
    disabled: disabledValues?.includes(cat) ?? false,
  }));
};

type SubProps = {
  field: BulkEditFieldType;
};

const Add = ({ field }: SubProps) => {
  return (
    <Form.Item
      name={`${field.id}_add`}
      rules={[{ required: true, message: 'New values required' }]}
    >
      <Select
        placeholder={getText('object_manager.bulk-edit-modal.add.placeholder')}
        allowClear={false}
        options={mapCategoriesToOptions(
          field.categories,
          field.disabledCategories
        )}
        {...getBulkEditSelectProps(field)}
      />
    </Form.Item>
  );
};

const FindAndReplace = ({ field }: SubProps) => {
  return (
    <>
      <BulkEditMatchRule fieldId={field.id} isMultiple={field.multiple} />
      <Form.Item
        name={`${field.id}_replace`}
        rules={[{ required: true, message: 'Value to find required' }]}
        label={getText(
          'object_manager.bulk-edit-modal.findReplace.find-input-label'
        )}
      >
        <Select
          placeholder={getText(
            'object_manager.bulk-edit-modal.placeholder.search'
          )}
          allowClear={false}
          mode={'multiple'}
          options={mapCategoriesToOptions(
            field.categories,
            field.disabledCategories
          )}
        />
      </Form.Item>
      <Form.Item
        name={`${field.id}_with`}
        rules={[{ required: true, message: 'Replacement value(s) required' }]}
        label={getText(
          'object_manager.bulk-edit-modal.findReplace.replace-input-label'
        )}
      >
        <Select
          placeholder={getText(
            'object_manager.bulk-edit-modal.placeholder.search'
          )}
          allowClear={false}
          options={mapCategoriesToOptions(
            field.categories,
            field.disabledCategories
          )}
          {...getBulkEditSelectProps(field)}
        />
      </Form.Item>
    </>
  );
};

const ReplaceAll = ({ field }: SubProps) => {
  return (
    <Form.Item
      name={`${field.id}_replaceAll`}
      rules={[{ required: true, message: 'New value(s) required' }]}
    >
      <Select
        placeholder={getText(
          'object_manager.bulk-edit-modal.replaceAll.placeholder'
        )}
        allowClear={false}
        options={mapCategoriesToOptions(
          field.categories,
          field.disabledCategories
        )}
        {...getBulkEditSelectProps(field)}
      />
    </Form.Item>
  );
};

const RemoveSpecific = ({ field }: SubProps) => {
  return (
    <Form.Item
      name={`${field.id}_removeSpecific`}
      rules={[{ required: true, message: 'Value(s) to remove required' }]}
    >
      <Select
        placeholder={getText(
          'object_manager.bulk-edit-modal.removeSpecific.placeholder'
        )}
        allowClear={false}
        mode="multiple"
        options={mapCategoriesToOptions(
          field.categories,
          field.disabledCategories
        )}
      />
    </Form.Item>
  );
};

export default BulkEditCategory;
